import { useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Box, Button, CircularProgress, IconButton, InputBase, Paper, Stack, Typography } from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import RestartAltRounded from "@mui/icons-material/RestartAltRounded";
import SendRounded from "@mui/icons-material/SendRounded";
import SmartToyOutlined from "@mui/icons-material/SmartToyOutlined";
import { colors } from "../constants/designTokens";
import { agenteSuggestions } from "../data/mockData";
import { logContabilidadeApiError, perguntarAgente } from "../services/agenteContabilidadeApiService";
import { getAgenteContabilidadeClientContext } from "../../../utils/agenteContabilidadeAccess";

const agenteProfileSrc = "/agente-profile.png";
const initialMessages = [
  {
    id: "welcome",
    role: "assistant",
    text: "Ola, sou o seu agente contabil especializado em Real Estate. Como posso ajuda-lo?",
  },
];

function getHistoryStorageKey() {
  const context = getAgenteContabilidadeClientContext();
  return `agentecontabilidade:${context.idCliente || "anon"}:${context.email || "anon"}:agente:history:v1`;
}

function normalizeVisibleAgentText(text) {
  return String(text || "")
    .replace(
      "Ola, sou o Agente, seu agente contabil especializado em Real Estate. Como posso ajuda-lo?",
      "Ola, sou o seu agente contabil especializado em Real Estate. Como posso ajuda-lo?"
    );
}

function readStoredMessages() {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(getHistoryStorageKey()) || "null");
    if (Array.isArray(parsed) && parsed.length) {
      return parsed
        .filter((item) => item?.id && item?.role && item?.text)
        .map((item) => ({ ...item, text: normalizeVisibleAgentText(item.text) }));
    }
  } catch (error) {
    // Ignore malformed session data and start a fresh conversation.
  }

  return initialMessages;
}

function writeStoredMessages(messages) {
  sessionStorage.setItem(getHistoryStorageKey(), JSON.stringify(messages.slice(-80)));
}

function sanitizeVisibleData(data) {
  if (!data || typeof data !== "object" || Array.isArray(data)) {
    return {};
  }

  return Object.fromEntries(Object.entries(data).slice(0, 80));
}

function buildAgentHistory(messages) {
  return messages
    .filter((message) => ["user", "assistant"].includes(message.role) && message.id !== "welcome" && message.text)
    .slice(-8)
    .map((message) => ({
      origem: message.role === "user" ? "usuario" : "agente",
      mensagem: String(message.text).slice(0, 2000),
    }));
}

function getErrorMessage(error) {
  const apiMessage = error?.response?.data?.mensagem || error?.response?.data?.message;
  if (apiMessage) return apiMessage;
  return "Nao foi possivel falar com o Agente agora. Tente novamente em instantes.";
}

export function AgenteFab({ onClick }) {
  return (
    <Button
      onClick={onClick}
      aria-label="Abrir assistente Agente"
      sx={{
        position: "fixed",
        right: { xs: 16, md: 28 },
        bottom: { xs: 16, md: 28 },
        minWidth: 0,
        height: 58,
        px: { xs: 1.3, sm: 1.8 },
        borderRadius: 999,
        bgcolor: colors.primary,
        color: "#fff",
        boxShadow: "0 18px 40px rgba(0,20,50,0.28)",
        textTransform: "none",
        zIndex: (theme) => theme.zIndex.drawer - 1,
        "&:hover": {
          bgcolor: colors.primaryDark,
          boxShadow: "0 20px 44px rgba(0,20,50,0.32)",
        },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1.2}>
        <Avatar
          alt="Agente"
          src={agenteProfileSrc}
          sx={{
            width: 34,
            height: 34,
            bgcolor: "#fff",
            border: "2px solid rgba(255,255,255,0.55)",
          }}
        />
        <Box sx={{ display: { xs: "none", sm: "block" }, textAlign: "left" }}>
          <Typography sx={{ fontSize: 13, fontWeight: 900, lineHeight: 1.1, color: "#fff" }}>
            Agente
          </Typography>
          <Typography sx={{ fontSize: 11, lineHeight: 1.1, color: "rgba(255,255,255,0.72)" }}>
            Agente contabil
          </Typography>
        </Box>
      </Stack>
    </Button>
  );
}

export default function AgentePanel({ onClose, docked = false, dragHandleProps, context }) {
  const { sx: dragHandleSx, ...resolvedDragHandleProps } = dragHandleProps || {};
  const [messages, setMessages] = useState(() => readStoredMessages());
  const [question, setQuestion] = useState("");
  const [status, setStatus] = useState("idle");
  const messagesEndRef = useRef(null);

  const resolvedContext = useMemo(
    () => ({
      paginaAtual: context?.paginaAtual || "",
      empresa: context?.empresa || "",
      competencia: context?.competencia || "",
      dadosVisiveis: sanitizeVisibleData(context?.dadosVisiveis),
    }),
    [context]
  );
  const hasUserMessages = messages.some((message) => message.role === "user");

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, status]);

  useEffect(() => {
    writeStoredMessages(messages);
  }, [messages]);

  function clearConversation() {
    setMessages(initialMessages);
    setQuestion("");
    setStatus("idle");
    sessionStorage.removeItem(getHistoryStorageKey());
  }

  async function sendQuestion(rawQuestion = question) {
    const trimmedQuestion = String(rawQuestion || "").trim().slice(0, 1000);
    if (!trimmedQuestion || status === "sending") {
      return;
    }

    const userMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      text: trimmedQuestion,
    };

    setMessages((current) => [...current, userMessage]);
    setQuestion("");
    setStatus("sending");

    try {
      const payload = {
        pergunta: trimmedQuestion,
        paginaAtual: resolvedContext.paginaAtual,
        empresa: resolvedContext.empresa || undefined,
        competencia: resolvedContext.competencia || undefined,
        dadosVisiveis: resolvedContext.dadosVisiveis,
        historico: buildAgentHistory(messages),
      };
      const response = await perguntarAgente(payload);
      const wasSuccessful = response?.sucesso !== false && Boolean(response?.resposta);
      const responseText = response?.resposta || response?.mensagem || "Nao encontrei uma resposta para essa pergunta.";

      if (!wasSuccessful) {
        console.warn("ContabilNow backend (agente sem resposta):", {
          payload,
          response,
        });
      }

      setMessages((current) => [
        ...current,
        {
          id: `assistant-${Date.now()}`,
          role: "assistant",
          text: responseText,
          error: !wasSuccessful,
        },
      ]);
      setStatus("idle");
    } catch (error) {
      logContabilidadeApiError("agente", error);
      setMessages((current) => [
        ...current,
        {
          id: `assistant-error-${Date.now()}`,
          role: "assistant",
          text: getErrorMessage(error),
          error: true,
        },
      ]);
      setStatus("idle");
    }
  }

  function handleKeyDown(event) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      sendQuestion();
    }
  }

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        height: "100%",
        bgcolor: colors.surface,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        boxShadow: docked ? "none" : "0 16px 42px rgba(0,20,50,0.22)",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        {...resolvedDragHandleProps}
        sx={{
          px: 2,
          py: 1.4,
          borderBottom: `1px solid ${colors.border}`,
          cursor: dragHandleProps ? "move" : "default",
          touchAction: dragHandleProps ? "none" : undefined,
          userSelect: dragHandleProps ? "none" : undefined,
          ...dragHandleSx,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.2}>
          <Avatar alt="Agente" src={agenteProfileSrc} sx={{ width: 40, height: 40 }} />
          <Box>
            <Typography sx={{ color: colors.text, fontWeight: 900 }}>Agente</Typography>
            <Typography sx={{ color: colors.softMuted, fontSize: 11 }}>Agente contabil - Real Estate</Typography>
          </Box>
        </Stack>
        <Stack direction="row" spacing={0.5}>
          <IconButton size="small" onClick={clearConversation} aria-label="Nova conversa com Agente">
            <RestartAltRounded fontSize="small" />
          </IconButton>
          <IconButton size="small" onClick={onClose} aria-label="Fechar assistente Agente">
            <CloseRounded fontSize="small" />
          </IconButton>
        </Stack>
      </Stack>

      <Stack spacing={1.2} sx={{ flex: 1, p: 2, overflowY: "auto" }}>
        {messages.map((message) => {
          const isUser = message.role === "user";
          return (
            <Stack key={message.id} direction="row" justifyContent={isUser ? "flex-end" : "flex-start"} spacing={1}>
              {!isUser ? (
                <Box sx={{ width: 30, height: 30, borderRadius: "50%", bgcolor: message.error ? colors.red : colors.green, color: "#fff", display: "grid", placeItems: "center", flexShrink: 0 }}>
                  <SmartToyOutlined sx={{ fontSize: 17 }} />
                </Box>
              ) : null}
              <Paper
                elevation={0}
                sx={{
                  maxWidth: "82%",
                  p: 1.35,
                  bgcolor: isUser ? colors.primary : colors.surfaceAlt,
                  color: isUser ? "#fff" : colors.text,
                  borderRadius: 2.4,
                  border: isUser ? "none" : `1px solid ${colors.border}`,
                }}
              >
                <Typography sx={{ fontSize: 13.5, lineHeight: 1.55, whiteSpace: "pre-wrap" }}>
                  {message.text}
                </Typography>
              </Paper>
            </Stack>
          );
        })}
        {status === "sending" ? (
          <Stack direction="row" spacing={1} alignItems="center">
            <CircularProgress size={18} />
            <Typography sx={{ color: colors.softMuted, fontSize: 12 }}>Agente analisando...</Typography>
          </Stack>
        ) : null}
        <Box ref={messagesEndRef} />
      </Stack>

      {!hasUserMessages ? (
        <Stack spacing={1} sx={{ px: 2 }}>
          {agenteSuggestions.map((text) => (
            <Button
              key={text}
              startIcon={<SmartToyOutlined />}
              disabled={status === "sending"}
              onClick={() => sendQuestion(text)}
              sx={{ justifyContent: "flex-start", border: `1px solid ${colors.border}`, borderRadius: 99, color: colors.muted, textTransform: "none", fontSize: 12 }}
            >
              {text}
            </Button>
          ))}
        </Stack>
      ) : null}

      <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 2, mt: 1 }}>
        <InputBase
          value={question}
          onChange={(event) => setQuestion(event.target.value.slice(0, 1000))}
          onKeyDown={handleKeyDown}
          placeholder="Pergunte ao Agente..."
          disabled={status === "sending"}
          multiline
          maxRows={3}
          sx={{ flex: 1, minHeight: 44, px: 1.8, py: 0.8, borderRadius: 2, border: `1px solid ${colors.border}`, color: colors.text }}
        />
        <IconButton
          disabled={!question.trim() || status === "sending"}
          onClick={() => sendQuestion()}
          sx={{ width: 44, height: 44, bgcolor: "#74d5bd", color: "#fff", "&:hover": { bgcolor: "#55c6aa" }, "&.Mui-disabled": { bgcolor: colors.surfaceSoft, color: colors.softMuted } }}
        >
          <SendRounded />
        </IconButton>
      </Stack>
    </Paper>
  );
}
